import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { Mic, Square, RotateCcw, Send } from "lucide-react";

const MAX_SECONDS = 300;

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export function BriefRecorder() {
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [societe, setSociete] = useState("");
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        stream.getTracks().forEach((t) => t.stop());
        setPhase("preview");
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setPhase("rec");
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      setError("Micro indisponible. Autorisez l'accès au micro dans votre navigateur.");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const reset = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl("");
    setSeconds(0);
    setPhase("idle");
    setError("");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!blob) return;
    setPhase("sending");
    setError("");
    try {
      const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `brief.${ext}`);
      fd.append("nom", nom);
      fd.append("email", email);
      fd.append("societe", societe);
      const res = await fetch(`${API}/brief`, { method: "POST", body: fd });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Envoi impossible pour le moment.");
      }
      setPhase("sent");
    } catch (err) {
      setError(err.message);
      setPhase("preview");
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (phase === "sent") {
    return (
      <div style={{ borderTop: "1px solid var(--li)", paddingTop: 28 }} data-testid="brief-confirmation">
        <p className="t-h3" style={{ margin: "0 0 10px", fontSize: 20 }}>Brief bien reçu.</p>
        <p className="t-body" style={{ margin: 0 }}>Votre enregistrement est en cours d'analyse. Nous préparons le rendez-vous et revenons vers vous sous 24 h ouvrées.</p>
      </div>
    );
  }

  return (
    <div data-testid="brief-recorder">
      <p className="t-body" style={{ margin: "0 0 22px", fontSize: 14, color: "var(--ink2)" }}>
        Écrire un brief, c'est long.<br />
        Parlez-nous plutôt : objectifs, cible, budget, délai.<br />
        On prépare, vous arbitrez.
      </p>

      {phase === "idle" && (
        <button type="button" className="btn btn-primary" onClick={start} data-testid="brief-record-start">
          <Mic size={14} strokeWidth={1.5} /> Enregistrer mon brief
        </button>
      )}

      {phase === "rec" && (
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <button type="button" className="btn btn-primary" onClick={stop} data-testid="brief-record-stop">
            <Square size={13} strokeWidth={1.5} /> Terminer
          </button>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 14 }} data-testid="brief-timer">
            <span style={{ width: 9, height: 9, background: "#8C1D18", borderRadius: "50%", animation: "brief-pulse 1.2s ease-in-out infinite" }} aria-hidden="true" />
            {fmt(seconds)} / 5:00
          </span>
        </div>
      )}

      {(phase === "preview" || phase === "sending") && (
        <form onSubmit={send} style={{ display: "grid", gap: 22 }} data-testid="brief-form">
          <audio controls src={blobUrl} style={{ width: "100%" }} data-testid="brief-audio-preview" />
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="field">
              <label htmlFor="b-nom">Nom</label>
              <input id="b-nom" data-testid="brief-nom" required value={nom} onChange={(e) => setNom(e.target.value)} autoComplete="name" />
            </div>
            <div className="field">
              <label htmlFor="b-societe">Société</label>
              <input id="b-societe" data-testid="brief-societe" value={societe} onChange={(e) => setSociete(e.target.value)} autoComplete="organization" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="b-email">Email pro</label>
            <input id="b-email" data-testid="brief-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button type="submit" className="btn btn-primary" disabled={phase === "sending"} data-testid="brief-send">
              <Send size={13} strokeWidth={1.5} /> {phase === "sending" ? "Envoi…" : "Envoyer le brief"}
            </button>
            <button type="button" className="btn" onClick={reset} data-testid="brief-retry">
              <RotateCcw size={13} strokeWidth={1.5} /> Recommencer
            </button>
          </div>
        </form>
      )}

      {error && <p className="t-body" style={{ color: "#8C1D18", margin: "16px 0 0" }} data-testid="brief-error">{error}</p>}
    </div>
  );
}
